import React, { useState, useEffect, useCallback } from 'react';
import { Search, MapPin, Globe, Menu, X, ShoppingBag, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import MegaMenu from './MegaMenu';
import MobileMenu from './MobileMenu';
import AuthModal from '../auth/AuthModal';
import CartDrawer from '../cart/CartDrawer';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import raymonLogo from '../../assets/brand/raymon-logo.svg';
import proomtbLogo from '../../assets/brand/proomtb-logo.svg';
import SearchPanel from './SearchPanel';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showMegaMenu, setShowMegaMenu] = useState(false); // For Accessories
    const [showEBikesMenu, setShowEBikesMenu] = useState(false); // For E-Bikes
    const [showBikesMenu, setShowBikesMenu] = useState(false); // For Normal Bikes
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

    const { setIsCartOpen, cartCount } = useCart();
    const { user, signOut } = useAuth();

    // Search State
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    const [isAdmin, setIsAdmin] = useState(false);
    const navigate = useNavigate();

    const openSearch = useCallback(() => {
        setIsMenuOpen(false);
        setIsSearchOpen(true);
    }, []);
    const closeSearch = useCallback(() => setIsSearchOpen(false), []);

    // "/" opens the search from any page, like on most stores
    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target.tagName || '').toLowerCase();
            if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey && tag !== 'input' && tag !== 'textarea' && !e.target.isContentEditable) {
                e.preventDefault();
                openSearch();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [openSearch]);

    // Check Admin Role
    React.useEffect(() => {
        const checkAdminRole = async () => {
            if (!user) {
                setIsAdmin(false);
                return;
            }

            try {
                const { data, error } = await import('../../supabaseClient').then(module =>
                    module.supabase
                        .from('profiles')
                        .select('Role')
                        .eq('id', user.id)
                        .single()
                );

                if (data?.Role === 'admin') {
                    setIsAdmin(true);
                } else {
                    setIsAdmin(false);
                }
            } catch (err) {
                console.error('Error checking admin role:', err);
                setIsAdmin(false);
            }
        };

        checkAdminRole();
    }, [user]);

    const handleUserClick = () => {
        if (user) {
            navigate('/profile');
        } else {
            setIsAuthModalOpen(true);
        }
    };

    return (
        <header className="fixed w-full top-0 z-50 bg-white border-b border-gray-100">
            <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-6 2xl:px-8">
                <div className="relative flex justify-between items-center h-20 xl:gap-4 2xl:gap-6">

                    {/* Logo: the store's own logo leads; Raymon is the brand it sells, so it stays small */}
                    <div className="flex-shrink-0 flex items-center">
                        <Link to="/" aria-label="PROO MTB & ROAD, inicio" className="flex items-center gap-2.5 md:gap-4">
                            <img src={proomtbLogo} alt="PROO MTB & ROAD" width="1065" height="244" className="h-7 sm:h-8 md:h-10 2xl:h-11 w-auto" />
                            <span className="h-5 md:h-7 w-px bg-gray-300" aria-hidden="true"></span>
                            <img src={raymonLogo} alt="Raymon" width="2820" height="349" className="h-2 sm:h-2.5 2xl:h-3 w-auto" />
                        </Link>
                    </div>

                    {/* Desktop Navigation (from xl; below that the links and campaigns live in the menu) */}
                    <nav className="hidden xl:flex space-x-3.5 2xl:space-x-5 items-center">
                        <div
                            className="group"
                            onMouseEnter={() => setShowEBikesMenu(true)}
                            onMouseLeave={() => setShowEBikesMenu(false)}
                        >
                            <Link
                                to="/ebikes"
                                onClick={() => setShowEBikesMenu(false)}
                                className="inline-flex items-center h-20 px-1 text-sm font-bold uppercase tracking-wider whitespace-nowrap hover:text-gray-600 transition-colors relative z-50"
                            >
                                E-Bikes
                            </Link>

                            {/* E-Bikes Mega Menu */}
                            <div className={`transition-opacity duration-200 ${showEBikesMenu ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                                {showEBikesMenu && <MegaMenu type="ebikes" onMouseLeave={() => setShowEBikesMenu(false)} />}
                            </div>
                        </div>

                        <div
                            className="group"
                            onMouseEnter={() => setShowBikesMenu(true)}
                            onMouseLeave={() => setShowBikesMenu(false)}
                        >
                            <Link
                                to="/bicicletas-analogas"
                                onClick={() => setShowBikesMenu(false)}
                                className="inline-flex items-center h-20 px-1 text-sm font-bold uppercase tracking-wider whitespace-nowrap hover:text-gray-600 transition-colors relative z-50"
                            >
                                Bicicletas análogas
                            </Link>

                            {/* Normal Bikes Mega Menu */}
                            <div className={`transition-opacity duration-200 ${showBikesMenu ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                                {showBikesMenu && <MegaMenu type="bikes" onMouseLeave={() => setShowBikesMenu(false)} />}
                            </div>
                        </div>
                        <div
                            className="group"
                            onMouseEnter={() => setShowMegaMenu(true)}
                            onMouseLeave={() => setShowMegaMenu(false)}
                        >
                            <Link
                                to="/accesorios"
                                className="inline-flex items-center h-20 px-1 text-sm font-bold uppercase tracking-wider whitespace-nowrap hover:text-gray-600 transition-colors relative z-50"
                            >
                                Accesorios
                            </Link>

                            {/* Mega Menu Container */}
                            <div className={`transition-opacity duration-200 ${showMegaMenu ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                                {showMegaMenu && <MegaMenu type="accessories" onMouseLeave={() => setShowMegaMenu(false)} />}
                            </div>
                        </div>

                        <Link
                            to="/navidad"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 2xl:px-3.5 2xl:py-2 whitespace-nowrap bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-white rounded-full text-[11px] 2xl:text-xs font-black uppercase tracking-wider transition-all shadow-[0_4px_12px_rgba(16,185,129,0.25)]"
                        >
                            <span>🎁 Bicis Navidad</span>
                        </Link>

                        <Link
                            to="/registro-aguinaldo"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 2xl:px-3.5 2xl:py-2 whitespace-nowrap bg-gradient-to-r from-red-600 to-rose-600 hover:brightness-110 text-white rounded-full text-[11px] 2xl:text-xs font-black uppercase tracking-wider transition-all shadow-[0_4px_12px_rgba(220,38,38,0.25)]"
                        >
                            <span>🎄 Aguinaldo</span>
                        </Link>
                    </nav>

                    {/* Utilities */}
                    <div className="hidden xl:flex items-center space-x-3.5 2xl:space-x-5 shrink-0">
                        <button className="hidden min-[1720px]:flex items-center text-xs font-bold uppercase tracking-wider hover:text-gray-600">
                            <Globe className="w-4 h-4 mr-1" />
                            ES
                        </button>
                        <a
                            href="https://www.google.com/maps/place/Proo+MTB+%26+Road/@18.472833,-69.9625717,17z/data=!3m1!4b1!4m6!3m5!1s0x8eaf8bf63c6292a5:0xeba7934faeefcc3a!8m2!3d18.4728279!4d-69.9599914!16s%2Fg%2F11rffl73cy?entry=ttu&g_ep=EgoyMDI2MDEwNy4wIKXMDSoASAFQAw%3D%3D"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Cómo llegar a la tienda"
                            className="hidden min-[1600px]:block text-gray-900 hover:text-gray-600"
                        >
                            <MapPin className="w-5 h-5" />
                        </a>
                        <button
                            type="button"
                            onClick={openSearch}
                            aria-label="Buscar productos"
                            className="min-[1680px]:hidden text-gray-900 hover:text-gray-600 p-1"
                        >
                            <Search className="w-5 h-5" />
                        </button>
                        <button
                            type="button"
                            onClick={openSearch}
                            aria-label="Buscar productos"
                            className="hidden min-[1680px]:flex items-center gap-2 h-10 w-44 min-[1780px]:w-56 px-4 rounded-full bg-gray-100 text-sm text-gray-500 hover:bg-gray-200 hover:text-black transition-colors"
                        >
                            <Search className="w-4 h-4 shrink-0" />
                            <span className="truncate">Buscar productos</span>
                        </button>

                        {/* Admin Dashboard Link */}
                        {isAdmin && (
                            <Link
                                to="/admin"
                                className="text-xs font-bold uppercase tracking-wider bg-black text-white px-2.5 py-1 rounded-full hover:bg-gray-800 transition-colors shrink-0"
                            >
                                Dashboard
                            </Link>
                        )}
                        <button
                            onClick={handleUserClick}
                            aria-label={user ? 'Mi cuenta' : 'Iniciar sesión'}
                            className="text-gray-900 hover:text-gray-600 relative shrink-0 p-1"
                        >
                            <User className={`w-5 h-5 ${user ? 'text-black fill-current' : ''}`} />
                        </button>
                        <button
                            onClick={() => setIsCartOpen(true)}
                            aria-label="Carrito de compras"
                            className="text-gray-900 hover:text-gray-600 relative shrink-0 p-1"
                        >
                            <ShoppingBag className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold h-4 w-4 flex items-center justify-center rounded-full pointer-events-none">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Mobile menu button (below sm the account lives inside the menu, so the logos keep their room) */}
                    <div className="xl:hidden flex items-center ml-auto pl-4 gap-3 sm:gap-4">
                        <button
                            type="button"
                            onClick={openSearch}
                            aria-label="Buscar productos"
                            className="text-gray-900 hover:text-gray-600"
                        >
                            <Search className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleUserClick}
                            aria-label={user ? 'Mi cuenta' : 'Iniciar sesión'}
                            className="hidden sm:block text-gray-900 hover:text-gray-600 relative"
                        >
                            <User className={`w-5 h-5 ${user ? 'text-black fill-current' : ''}`} />
                        </button>
                        <button
                            onClick={() => setIsCartOpen(true)}
                            className="text-gray-900 hover:text-gray-600 relative"
                        >
                            <ShoppingBag className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold h-4 w-4 flex items-center justify-center rounded-full">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                            className="text-gray-900 hover:text-gray-600 p-1.5 -mr-1.5"
                        >
                            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onSearch={openSearch} onAccount={() => { setIsMenuOpen(false); handleUserClick(); }} isLoggedIn={!!user} isAdmin={isAdmin} />

            {/* Auth Modal */}
            <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

            {/* Cart Drawer */}
            <CartDrawer />

            {/* Store search */}
            <SearchPanel open={isSearchOpen} onClose={closeSearch} />
        </header>
    );
};

export default Header;
